import type { AnalysisResult } from "@/types/analysis";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Info, Search, Settings, FileText, Bot, Key, Link2 } from "lucide-react";

const avg = (nums: number[]) => (nums.length ? Math.round(nums.reduce((a, b) => a + b, 0) / nums.length) : 0);

const RadialGauge = ({ score, size = 120 }: { score: number; size?: number }) => {
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? "hsl(var(--success))" : score >= 40 ? "hsl(var(--warning))" : "hsl(var(--destructive))";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-3xl font-bold text-foreground">{score}</span>
      </div>
    </div>
  );
};

const getQualityLabel = (score: number) => {
  if (score >= 80) return { text: "Excellent", className: "text-success" };
  if (score >= 60) return { text: "Good", className: "text-success" };
  if (score >= 40) return { text: "Needs Work", className: "text-warning" };
  return { text: "Poor", className: "text-destructive" };
};

const getProgressColor = (score: number) => {
  if (score >= 70) return "[&>div]:bg-success";
  if (score >= 40) return "[&>div]:bg-warning";
  return "[&>div]:bg-destructive";
};

const MetricCard = ({ icon: Icon, label, score }: { icon: React.ElementType; label: string; score: number }) => (
  <Card className="border-border bg-card hover:bg-accent/30 transition-colors">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-lg font-bold text-foreground">{score}</span>
      </div>
      <Progress value={score} className={`h-1.5 mb-2 ${getProgressColor(score)}`} />
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
    </CardContent>
  </Card>
);

export const AverageScoreOverview = ({ results }: { results: AnalysisResult[] }) => {
  const overallScore = avg(results.map((r) => r.overallScore));
  const confidence = avg(results.map((r) => r.overallConfidence));
  const quality = getQualityLabel(overallScore);

  const onPage = avg(results.map((r) => r.seoAudit.onPage.score));
  const technical = avg(results.map((r) => r.seoAudit.technical.score));
  const content = avg(results.map((r) => r.seoAudit.contentQuality.score));
  const aiVis = avg(results.map((r) => r.aiVisibility.overallScore));

  const kwResults = results.filter((r) => r.keywordAnalysis);
  const kwScore = kwResults.length ? avg(kwResults.map((r) => r.keywordAnalysis!.overallScore)) : null;

  const linkResults = results.filter((r) => r.linkIntelligence);
  const linkScore = linkResults.length
    ? avg(
        linkResults.map((r) => {
          const li = r.linkIntelligence!;
          const authorityScore = li.estimatedReferringDomainsForPage1 > 0
            ? Math.min(100, Math.round((li.currentEstimatedReferringDomains / li.estimatedReferringDomainsForPage1) * 100))
            : 100;
          const factors = [li.internalLinkingScore, authorityScore];
          if (li.externalLinkQualityScore != null) factors.push(li.externalLinkQualityScore);
          return Math.round(factors.reduce((a, b) => a + b, 0) / factors.length);
        })
      )
    : null;

  return (
    <Card className="border-border bg-card">
      <CardContent className="p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
          <RadialGauge score={overallScore} size={140} />
          <div className="text-center sm:text-left">
            <h2 className="text-lg font-semibold text-foreground">Average Visibility</h2>
            <p className={`text-sm font-medium ${quality.className}`}>{quality.text}</p>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 gap-1 mt-2">
                  <Info className="w-2.5 h-2.5" />
                  {confidence}% avg confidence
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Average confidence across all analyses</TooltipContent>
            </Tooltip>
            <p className="mt-2 text-xs text-muted-foreground">
              Across {results.length} analyzed site{results.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <MetricCard icon={Search} label="On-Page SEO" score={onPage} />
          <MetricCard icon={Settings} label="Technical SEO" score={technical} />
          <MetricCard icon={FileText} label="Content Quality" score={content} />
          <MetricCard icon={Bot} label="AI Visibility" score={aiVis} />
          {kwScore !== null && <MetricCard icon={Key} label="Keyword Optimization" score={kwScore} />}
          {linkScore !== null && <MetricCard icon={Link2} label="Link Intelligence" score={linkScore} />}
        </div>
      </CardContent>
    </Card>
  );
};
